import type { LucideIcon } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { cn } from "@/modern/utils";

import { NOTIFICATION_TILE_MOTION_CLS } from "../constants/layout";
import { toneStyle, TILE_SURFACE_CLS, TILE_MUTED_SURFACE_CLS } from "../constants/tones";

export function NotificationTileButton({
  onClick,
  eyebrow,
  value,
  caption,
  icon: Icon,
  classes,
  muted,
  big,
  className,
  span,
  children,
}: {
  onClick?: () => void;
  eyebrow: string;
  value: ReactNode;
  caption: string;
  icon?: LucideIcon;
  classes?: { bg: string; text: string; ring: string };
  muted?: boolean;
  big?: boolean;
  className?: string;
  span?: string;
  children?: ReactNode;
}) {
  const style: CSSProperties | undefined =
    muted || !classes ? undefined : toneStyle(classes.bg);

  return (
    <button
      type="button"
      onClick={onClick}
      style={style}
      className={cn(
        "group relative flex flex-col justify-between overflow-hidden rounded-[1.5rem] p-5 text-left",
        NOTIFICATION_TILE_MOTION_CLS,
        muted ? TILE_MUTED_SURFACE_CLS : TILE_SURFACE_CLS,
        big ? "min-h-[11rem]" : "min-h-[7.5rem]",
        span,
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-foreground/70">
          {eyebrow}
        </span>
        {Icon ? (
          <Icon
            className={cn(big ? "size-6" : "size-4", "shrink-0 opacity-80")}
            aria-hidden
          />
        ) : null}
      </div>
      <div className="mt-auto">
        <div
          className={cn(
            "font-bold leading-[0.95] tracking-tight",
            big ? "text-5xl sm:text-6xl" : "text-4xl",
          )}
        >
          {value}
        </div>
        <p className="mt-2 text-[13px] font-medium text-foreground/75">
          {caption}
        </p>
        {children}
      </div>
    </button>
  );
}
